/** Configuration — Lifecycle Control — Archive movement Rules (Moderation / Compliance / F&S). */

export const LIFECYCLE_ROUTE = '/admin/configuration/lifecycle';

/** Current coverage: three case Archives only. */
export const LIFECYCLE_MODULES = [
  { value: 'moderation', label: 'Moderation' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'fraud_security', label: 'Fraud & Security' },
];

export const RECORD_TYPES_BY_MODULE = {
  moderation: [{ value: 'moderation_case', label: 'Moderation Case' }],
  compliance: [{ value: 'compliance_case', label: 'Compliance Case' }],
  fraud_security: [{ value: 'fraud_security_case', label: 'Fraud & Security Case' }],
};

/**
 * Illustrative End Events — complete registered definitions NOT CONFIRMED.
 * Labels carry section 3 meanings; permanent production IDs are not allocated here.
 */
export const END_EVENT_OPTIONS = [
  {
    value: 'moderation_decision_work_complete',
    label: 'Decision and required work complete',
    module: 'moderation',
    recordType: 'moderation_case',
    meaning:
      'Valid final decision and required source results complete; case Decided.',
  },
  {
    value: 'compliance_decision_work_complete',
    label: 'Decision and required work complete',
    module: 'compliance',
    recordType: 'compliance_case',
    meaning:
      'Final decision and required Business Record completion work complete; case Decided.',
  },
  {
    value: 'fraud_outcome_work_notice_available',
    label: 'Outcome work complete and notice available',
    module: 'fraud_security',
    recordType: 'fraud_security_case',
    meaning:
      'Confirmed decision’s required outcome work, including applicable account actions, case-specific releases and visibility results, complete; outcome notice available.',
  },
];

/**
 * Illustrative Archive Actions — complete registered definitions NOT CONFIRMED.
 * Destination is derived and displayed read-only.
 */
export const ARCHIVE_ACTION_OPTIONS = [
  {
    value: 'move_to_moderation_archive',
    label: 'Move to Moderation Archive',
    module: 'moderation',
    recordType: 'moderation_case',
    coveredMaterial: 'The completed Moderation case and its approved owned case material.',
    completionRequirements:
      'Valid final decision and required source results complete; case Decided.',
    destination: 'Moderation Archive',
    references: [
      { id: 'REF-MOD-CASE', label: 'Moderation Case ID', available: true },
      { id: 'REF-MOD-FLAG', label: 'Originating Flag ID', available: true },
      { id: 'REF-MOD-DEC', label: 'Moderation Decision ID', available: true },
    ],
  },
  {
    value: 'move_to_compliance_archive',
    label: 'Move to Compliance Archive',
    module: 'compliance',
    recordType: 'compliance_case',
    coveredMaterial: 'The completed Compliance case and its approved owned case material.',
    completionRequirements:
      'Final decision and required Business Record completion work complete; case Decided.',
    destination: 'Compliance Archive',
    references: [
      { id: 'REF-CMP-CASE', label: 'CMP Case ID', available: true },
      { id: 'REF-CMP-DEC', label: 'CMP Decision ID', available: true },
    ],
  },
  {
    value: 'move_to_fraud_security_archive',
    label: 'Move to Fraud & Security Archive',
    module: 'fraud_security',
    recordType: 'fraud_security_case',
    coveredMaterial: 'The completed Fraud & Security case and its approved owned case material.',
    completionRequirements:
      'Confirmed decision’s required outcome work complete; outcome notice available.',
    destination: 'Fraud & Security Archive',
    references: [
      { id: 'REF-FSA', label: 'FSA Archive Case ID (assigned on first movement)', available: true },
      { id: 'REF-DEC-FS', label: 'Fraud & Security Decision ID', available: true },
    ],
  },
];

/** @deprecated aliases for older rows / test simulation */
export const LIFECYCLE_TRIGGER_OPTIONS = END_EVENT_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
}));
export const ARCHIVE_RULE_OPTIONS = ARCHIVE_ACTION_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
}));
export const RESTORE_RULE_OPTIONS = [
  { value: 'not_applicable', label: 'Not Applicable' },
];
export const HOLD_EFFECT_OPTIONS = [
  { value: 'no_hold_effect', label: 'No Hold Effect' },
];
export const RETENTION_HANDOFF_OPTIONS = [
  { value: 'when_eligible', label: 'When Eligible' },
];
export const LIFECYCLE_RESULT_OPTIONS = [
  { value: 'archived', label: 'Archived' },
];
export const LIFECYCLE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'inactive', label: 'Inactive' },
];

export const LIFECYCLE_DEFERRED_DEFINITIONS = [
  {
    id: 'end_event_catalogue',
    label: 'Complete registered End Event definitions',
    status: 'NOT CONFIRMED',
  },
  {
    id: 'archive_action_catalogue',
    label: 'Complete registered Archive Action definitions',
    status: 'NOT CONFIRMED',
  },
  {
    id: 'condition_catalogue',
    label: 'Optional Lifecycle Condition catalogue',
    status: 'NOT DEFINED',
  },
];

const LEGACY_MODULE_MAP = {
  documents: 'compliance',
};

const LEGACY_RECORD_TYPE_MAP = {
  case: 'moderation_case',
  case_record: 'moderation_case',
  appeal_case: 'moderation_case',
  appeal_record: 'moderation_case',
  licence_record: 'compliance_case',
  badge_record: 'compliance_case',
  trade_ticket_record: 'compliance_case',
  compliance_record: 'compliance_case',
  restriction_record: 'fraud_security_case',
  fraud_case: 'fraud_security_case',
};

const LEGACY_END_EVENT_MAP = {
  moderation_final_decision: 'moderation_decision_work_complete',
  compliance_final_decision: 'compliance_decision_work_complete',
  fraud_finalisation: 'fraud_outcome_work_notice_available',
  on_closure: '',
  on_replacement: '',
  on_expiry: '',
  after_inactivity: '',
  on_account_closure: '',
  on_removal: '',
};

const LEGACY_ARCHIVE_ACTION_MAP = {
  archive_moderation_case: 'move_to_moderation_archive',
  archive_compliance_case: 'move_to_compliance_archive',
  archive_fraud_case: 'move_to_fraud_security_archive',
  archive: '',
  do_not_archive: '',
  archive_immediately: '',
  archive_after_time_period: '',
};

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  return options.find((o) => o.value === value)?.label ?? String(value);
}

export function normalizeLifecycleModule(value) {
  if (!value) return '';
  return LEGACY_MODULE_MAP[value] || value;
}

export function normalizeLifecycleRecordType(value, module) {
  if (!value) return '';
  const mod = normalizeLifecycleModule(module);
  if (value === 'case' || value === 'case_record') {
    if (mod === 'fraud_security') return 'fraud_security_case';
    if (mod === 'moderation') return 'moderation_case';
  }
  if (value === 'appeal_case' && mod === 'fraud_security') return 'fraud_security_case';
  return LEGACY_RECORD_TYPE_MAP[value] || value;
}

export function normalizeEndEvent(value) {
  if (!value) return '';
  if (END_EVENT_OPTIONS.some((o) => o.value === value)) return value;
  return LEGACY_END_EVENT_MAP[value] ?? value;
}

/** @deprecated use normalizeEndEvent */
export function normalizeLifecycleTrigger(value) {
  return normalizeEndEvent(value);
}

export function normalizeArchiveAction(value) {
  if (!value) return '';
  if (ARCHIVE_ACTION_OPTIONS.some((o) => o.value === value)) return value;
  return LEGACY_ARCHIVE_ACTION_MAP[value] ?? value;
}

/** @deprecated use normalizeArchiveAction */
export function normalizeArchiveRule(value) {
  return normalizeArchiveAction(value);
}

export function normalizeRestoreRule(value) {
  return value || 'not_applicable';
}

export function normalizeHoldEffect(value) {
  return value || 'no_hold_effect';
}

export function normalizeRetentionHandoff(value) {
  return value || 'when_eligible';
}

export function normalizeLifecycleResult(value) {
  return value || 'archived';
}

export function normalizeLifecycleStatus() {
  return '';
}

export function normalizeLifecycleRuleScope(module, recordType) {
  const mod = normalizeLifecycleModule(module);
  return {
    module: mod,
    recordType: normalizeLifecycleRecordType(recordType, mod),
  };
}

export function getLifecycleModuleLabel(v) {
  return labelFromOptions(LIFECYCLE_MODULES, normalizeLifecycleModule(v));
}

export function getRecordTypeLabel(module, recordType) {
  const mod = normalizeLifecycleModule(module);
  const rt = normalizeLifecycleRecordType(recordType, mod);
  const list = RECORD_TYPES_BY_MODULE[mod] || [];
  return labelFromOptions(list, rt);
}

export function recordTypesForModule(module) {
  return RECORD_TYPES_BY_MODULE[normalizeLifecycleModule(module)] || [];
}

export function getEndEventDefinition(value) {
  const normalized = normalizeEndEvent(value);
  return END_EVENT_OPTIONS.find((o) => o.value === normalized) || null;
}

export function getArchiveActionDefinition(value) {
  const normalized = normalizeArchiveAction(value);
  return ARCHIVE_ACTION_OPTIONS.find((o) => o.value === normalized) || null;
}

export function getEndEventLabel(v) {
  const def = getEndEventDefinition(v);
  if (def) return def.label;
  if (!v) return '';
  return `${v} (Unavailable)`;
}

/** @deprecated use getEndEventLabel */
export function getLifecycleTriggerLabel(v) {
  return getEndEventLabel(v);
}

export function getArchiveActionLabel(v) {
  const def = getArchiveActionDefinition(v);
  if (def) return def.label;
  if (!v) return '';
  return `${v} (Unavailable)`;
}

/** @deprecated use getArchiveActionLabel */
export function getArchiveRuleLabel(v) {
  return getArchiveActionLabel(v);
}

export function getRestoreRuleLabel(v) {
  return labelFromOptions(RESTORE_RULE_OPTIONS, v) || v || '—';
}

export function getHoldEffectLabel(v) {
  return labelFromOptions(HOLD_EFFECT_OPTIONS, v) || v || '—';
}

export function getRetentionHandoffLabel(v) {
  return labelFromOptions(RETENTION_HANDOFF_OPTIONS, v) || v || '—';
}

export function getLifecycleResultLabel(v) {
  return labelFromOptions(LIFECYCLE_RESULT_OPTIONS, v) || v || '—';
}

export function getLifecycleStatusLabel() {
  return '—';
}

export function getEndEventsForScope(module, recordType) {
  const scope = normalizeLifecycleRuleScope(module, recordType);
  return END_EVENT_OPTIONS.filter(
    (o) => o.module === scope.module && o.recordType === scope.recordType,
  ).map((o) => ({ value: o.value, label: o.label }));
}

/** @deprecated use getEndEventsForScope */
export function getLifecycleTriggersForScope(module, recordType) {
  return getEndEventsForScope(module, recordType);
}

export function getArchiveActionsForScope(module, recordType, endEvent) {
  const scope = normalizeLifecycleRuleScope(module, recordType);
  const event = normalizeEndEvent(endEvent);
  return ARCHIVE_ACTION_OPTIONS.filter((o) => {
    if (o.module !== scope.module || o.recordType !== scope.recordType) return false;
    if (!event) return true;
    const eventDef = getEndEventDefinition(event);
    return eventDef && eventDef.module === o.module && eventDef.recordType === o.recordType;
  }).map((o) => ({ value: o.value, label: o.label }));
}

export function getRestoreRulesForArchive() {
  return RESTORE_RULE_OPTIONS;
}

export function getLifecycleResultsForArchive() {
  return LIFECYCLE_RESULT_OPTIONS;
}

/** Optional Condition catalogue is NOT DEFINED. */
export function getAvailableLifecycleConditionFields() {
  return [];
}

export function createEmptyLifecycleCondition() {
  return { field: '', operator: '', value: '', values: [] };
}

export function createEmptyLifecycleForm() {
  return {
    id: '',
    ruleName: '',
    module: '',
    recordType: '',
    endEvent: '',
    lifecycleTrigger: '',
    archiveAction: '',
    archiveRule: '',
    conditions: [],
    changeReason: '',
    dateAdded: '',
    addedBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
    createdOn: '',
    createdBy: '',
    modifiedOn: '',
    modifiedBy: '',
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

export function normalizeLifecycleForm(raw = {}) {
  const scope = normalizeLifecycleRuleScope(raw.module, raw.recordType);
  const endEvent = normalizeEndEvent(raw.endEvent || raw.lifecycleTrigger);
  const archiveAction = normalizeArchiveAction(raw.archiveAction || raw.archiveRule);
  return {
    ...createEmptyLifecycleForm(),
    ...raw,
    module: scope.module,
    recordType: scope.recordType,
    endEvent,
    lifecycleTrigger: endEvent,
    archiveAction,
    archiveRule: archiveAction,
    conditions: Array.isArray(raw.conditions) ? raw.conditions : [],
    ruleName: raw.ruleName || '',
    changeReason: raw.changeReason || '',
    dateAdded: raw.dateAdded || raw.createdOn || '',
    addedBy: raw.addedBy || raw.createdBy || '',
    lastUpdated: raw.lastUpdated || raw.modifiedOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.modifiedBy || '',
  };
}

function conditionSetKey(conditions = []) {
  if (!Array.isArray(conditions) || !conditions.length) return 'no_conditions';
  return [...conditions]
    .map((c) => {
      const field = String(c.field || c.name || '').trim().toLowerCase();
      const op = String(c.operator || '').trim().toLowerCase();
      const val = Array.isArray(c.values)
        ? [...c.values].map((v) => String(v).trim().toLowerCase()).sort().join(',')
        : String(c.value || '').trim().toLowerCase();
      return `${field}|${op}|${val}`;
    })
    .sort()
    .join('||');
}

export function buildLifecycleScopeKey(rule) {
  const scope = normalizeLifecycleRuleScope(rule.module, rule.recordType);
  const event = normalizeEndEvent(rule.endEvent || rule.lifecycleTrigger);
  return `${scope.module}|${scope.recordType}|${event}`;
}

export function lifecycleRuleDuplicateKey(rule) {
  const scopeKey = buildLifecycleScopeKey(rule);
  const action = normalizeArchiveAction(rule.archiveAction || rule.archiveRule);
  return `${scopeKey}|${action}|${conditionSetKey(rule.conditions)}`;
}

export function findDuplicateLifecycleRule(rules, candidate, excludeId) {
  const key = lifecycleRuleDuplicateKey(candidate);
  return (rules || []).find(
    (r) => r.id !== excludeId && lifecycleRuleDuplicateKey(r) === key,
  );
}

/** Same End Event overlap — No Conditions covers the whole scope. */
export function findOverlappingLifecycleRule(rules, candidate, excludeId) {
  const scopeKey = buildLifecycleScopeKey(candidate);
  const candConditions = conditionSetKey(candidate.conditions);
  return (rules || []).find((r) => {
    if (r.id === excludeId) return false;
    if (buildLifecycleScopeKey(r) !== scopeKey) return false;
    const other = conditionSetKey(r.conditions);
    if (candConditions === 'no_conditions' || other === 'no_conditions') return true;
    return candConditions === other;
  });
}

/** Same End Event with different Archive Action. */
export function findConflictingLifecycleRule(rules, candidate, excludeId) {
  const scopeKey = buildLifecycleScopeKey(candidate);
  const action = normalizeArchiveAction(candidate.archiveAction || candidate.archiveRule);
  const candConditions = conditionSetKey(candidate.conditions);
  return (rules || []).find((r) => {
    if (r.id === excludeId) return false;
    if (buildLifecycleScopeKey(r) !== scopeKey) return false;
    if (normalizeArchiveAction(r.archiveAction || r.archiveRule) === action) return false;
    const other = conditionSetKey(r.conditions);
    if (candConditions === 'no_conditions' || other === 'no_conditions') return true;
    return candConditions === other;
  });
}

export function formatLifecycleConditionsDisplay(rule) {
  const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
  if (!conditions.length) return { text: 'No Conditions', extraCount: 0, lines: [] };
  const lines = conditions.map((c) => {
    const field = c.fieldLabel || c.field || c.name || 'Condition';
    const op = c.operatorLabel || c.operator || '';
    const val = c.valueLabel || c.value || (Array.isArray(c.values) ? c.values.join(', ') : '');
    return [field, op, val].filter(Boolean).join(' · ');
  });
  if (lines.length === 1) return { text: lines[0], extraCount: 0, lines };
  return {
    text: `${lines[0]} · +${lines.length - 1} more`,
    extraCount: lines.length - 1,
    lines,
  };
}

export function formatLifecycleDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${suffix}`;
}

export function sortLifecycleRules(list) {
  return [...(list || [])].sort((a, b) => {
    const aTime = new Date(a.lastUpdated || a.modifiedOn || a.dateAdded || a.createdOn || 0).getTime();
    const bTime = new Date(b.lastUpdated || b.modifiedOn || b.dateAdded || b.createdOn || 0).getTime();
    return bTime - aTime;
  });
}

export function nextLifecycleRuleId(rules) {
  const nums = (Array.isArray(rules) ? rules : [])
    .map((r) => String(r?.id || '').match(/^LFC-(\d+)$/i))
    .filter(Boolean)
    .map((m) => Number(m[1]))
    .filter((n) => Number.isFinite(n));
  const nextNum = (nums.length ? Math.max(...nums) : 0) + 1;
  return `LFC-${String(nextNum).padStart(4, '0')}`;
}

export function buildSampleLifecycleRules() {
  const samples = [
    {
      id: 'LFC-0001',
      ruleName: 'Archive Moderation Case',
      module: 'moderation',
      recordType: 'moderation_case',
      endEvent: 'moderation_decision_work_complete',
      archiveAction: 'move_to_moderation_archive',
      conditions: [],
      changeReason: 'Preserve completed Moderation cases in their supported Archive.',
      createdOn: '2026-08-18T09:20:00+10:00',
      createdBy: 'Olivia Brown',
      modifiedOn: '2026-08-18T09:20:00+10:00',
      modifiedBy: 'Olivia Brown',
    },
    {
      id: 'LFC-0002',
      ruleName: 'Archive Compliance Case',
      module: 'compliance',
      recordType: 'compliance_case',
      endEvent: 'compliance_decision_work_complete',
      archiveAction: 'move_to_compliance_archive',
      conditions: [],
      changeReason: 'Preserve completed Compliance cases in their supported Archive.',
      createdOn: '2026-08-17T10:15:00+10:00',
      createdBy: 'Olivia Brown',
      modifiedOn: '2026-08-17T10:15:00+10:00',
      modifiedBy: 'Olivia Brown',
    },
    {
      id: 'LFC-0003',
      ruleName: 'Archive Fraud & Security Case',
      module: 'fraud_security',
      recordType: 'fraud_security_case',
      endEvent: 'fraud_outcome_work_notice_available',
      archiveAction: 'move_to_fraud_security_archive',
      conditions: [],
      changeReason: 'Preserve completed Fraud & Security cases in their supported Archive.',
      createdOn: '2026-08-16T11:40:00+10:00',
      createdBy: 'Olivia Brown',
      modifiedOn: '2026-08-16T11:40:00+10:00',
      modifiedBy: 'Olivia Brown',
    },
  ];

  return samples.map((r) => {
    const normalized = normalizeLifecycleForm({
      ...r,
      dateAdded: r.createdOn,
      addedBy: r.createdBy,
      lastUpdated: r.modifiedOn,
      lastUpdatedBy: r.modifiedBy,
    });
    return {
      ...normalized,
      version: 1,
      pendingDraft: null,
      configurationHistory: [],
      testHistory: [],
    };
  });
}

/** @deprecated */
export function getStoredLifecycleRules() {
  return buildSampleLifecycleRules();
}

/** @deprecated */
export function saveStoredLifecycleRules() {}

/** @deprecated */
export function ensureLifecycleRulesSeeded() {
  return buildSampleLifecycleRules();
}

/** @deprecated */
export function expandLifecycleRuleFromList(row) {
  return normalizeLifecycleForm(row || {});
}

/** @deprecated */
export function buildDummyLifecycleRule(ruleId) {
  return buildSampleLifecycleRules().find((r) => r.id === ruleId) || null;
}

/** @deprecated */
export function findLifecycleRuleConflict(rules, candidate, excludeId) {
  return findConflictingLifecycleRule(rules, candidate, excludeId);
}

/** @deprecated */
export const RETENTION_RULE_OPTIONS = RETENTION_HANDOFF_OPTIONS;
export const DELETION_RULE_OPTIONS = [];
export const HOLD_RULE_OPTIONS = HOLD_EFFECT_OPTIONS;
export const ACCOUNT_CLOSURE_RESULT_OPTIONS = [];
export function getRetentionRuleLabel(v) {
  return getRetentionHandoffLabel(v);
}
export function getDeletionRuleLabel() {
  return '—';
}
export function getHoldRuleLabel(v) {
  return getHoldEffectLabel(v);
}
export function getAccountClosureResultLabel() {
  return '—';
}
